import React, { useMemo, useState } from "react";
import { Box, Button, IconButton, InputAdornment, Popover, TextField, Typography, makeStyles, useTheme } from "@material-ui/core";
import { Event as CalendarIcon, AccessTime as TimeIcon } from "@material-ui/icons";
import { ThemeProvider as Mui5ThemeProvider, createTheme as createMui5Theme } from "@mui/material/styles";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR, enUS, es } from "date-fns/locale";
import { ptBR as pickerPtBR, enUS as pickerEnUS, esES as pickerEsES } from "@mui/x-date-pickers/locales";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  field: { "& input::-webkit-calendar-picker-indicator": { display: "none" } },
  paper: { borderRadius: 12, maxWidth: "calc(100vw - 32px)", backgroundColor: theme.palette.background.paper },
  panel: { padding: theme.spacing(1), width: 320, maxWidth: "calc(100vw - 32px)" },
  actions: { display: "flex", justifyContent: "space-between", gap: theme.spacing(1), padding: theme.spacing(0, 1, 1) },
  time: { padding: theme.spacing(0, 1, 1) },
}));

const localDate = value => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  return date.getFullYear() === Number(match[1]) && date.getMonth() === Number(match[2]) - 1 && date.getDate() === Number(match[3]) ? date : null;
};
const dateValue = date => date && `${date.getFullYear().toString().padStart(4, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const formatTime = value => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};
let nextFieldId = 0;

// Keep the native input and its local string contract; only the picker surface is shared.
export default function DateTimeField({ type = "date", value, onChange, InputProps, inputProps, disabled, name, ...props }) {
  const classes = useStyles();
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const [day, setDay] = useState(null);
  const [time, setTime] = useState("");
  const [generatedId] = useState(() => `neo-date-field-${++nextFieldId}`);
  const fieldId = props.id || generatedId;
  const isTime = type === "time";
  const hasTime = type === "datetime-local" || isTime;
  const language = String(i18n.language || "pt").split("-")[0];
  const locale = language === "es" ? es : language === "en" ? enUS : ptBR;
  const localeText = (language === "es" ? pickerEsES : language === "en" ? pickerEnUS : pickerPtBR)
    .components.MuiLocalizationProvider.defaultProps.localeText;
  const modernTheme = useMemo(() => createMui5Theme({ palette: {
    mode: theme.palette.type || "light", primary: { main: theme.palette.primary.main },
    background: { default: theme.palette.background.default, paper: theme.palette.background.paper },
    text: { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary },
  }, typography: { fontFamily: theme.typography.fontFamily } }), [theme]);
  const openPicker = event => {
    if (disabled || inputProps?.readOnly) return;
    setDay(localDate(value));
    setTime(isTime ? String(value || "").slice(0, 5) : String(value || "").split("T")[1]?.slice(0, 5) || "");
    setAnchor(event.currentTarget);
  };
  const emit = next => {
    if (onChange) {
      const target = { name, value: next, type };
      onChange({ target, currentTarget: target, persist: () => {} });
    }
    setAnchor(null);
  };
  const timeValid = !hasTime || /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
  const canApply = (isTime || day) && timeValid;
  const minimum = localDate(inputProps?.min || props.min);
  const maximum = localDate(inputProps?.max || props.max);
  const handleInputClick = event => {
    if (inputProps && inputProps.onClick) inputProps.onClick(event);
    if (event.defaultPrevented || disabled || inputProps?.readOnly) return;
    event.preventDefault();
    openPicker(event);
  };

  return <>
    <TextField {...props} id={fieldId} name={name} type={type} value={value == null ? "" : value} disabled={disabled}
      onChange={onChange} inputProps={{ ...inputProps, onClick: handleInputClick }} className={[props.className, classes.field].filter(Boolean).join(" ")}
      InputProps={{ ...InputProps, endAdornment: <>
        {InputProps?.endAdornment}
        <InputAdornment position="end">
          <IconButton size="small" disabled={disabled || inputProps?.readOnly} onClick={openPicker}
            aria-label={i18n.t(isTime ? "datePicker.openTime" : "datePicker.openDate")}>
            {isTime ? <TimeIcon fontSize="small" /> : <CalendarIcon fontSize="small" />}
          </IconButton>
        </InputAdornment>
      </> }} />
    <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}
      classes={{ paper: classes.paper }}>
      <Box className={classes.panel} role="dialog" aria-label={i18n.t(isTime ? "datePicker.openTime" : "datePicker.openDate")}>
        {!isTime && <Mui5ThemeProvider theme={modernTheme}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale} localeText={localeText}>
            <DateCalendar value={day} onChange={setDay} minDate={minimum || new Date(100, 0, 1)}
              maxDate={maximum || new Date(9999, 11, 31)} sx={{ width: "100%", maxHeight: 320 }} />
          </LocalizationProvider>
        </Mui5ThemeProvider>}
        {hasTime && <Box className={classes.time}>
          <TextField id={`${fieldId}-picker-time`} label={i18n.t("datePicker.time")} type="text" variant="outlined" size="small" fullWidth
            value={time} onChange={event => setTime(formatTime(event.target.value))} placeholder="HH:mm"
            inputProps={{ inputMode: "numeric", maxLength: 5 }} error={Boolean(time) && !timeValid} />
          {!timeValid && <Typography variant="caption" color="textSecondary">{i18n.t("datePicker.timeHint")}</Typography>}
        </Box>}
        <Box className={classes.actions}>
          <Button size="small" onClick={() => emit("")}>{i18n.t("datePicker.clear")}</Button>
          <Button size="small" color="primary" variant="contained" disabled={!canApply}
            onClick={() => emit(isTime ? time : `${dateValue(day)}${hasTime ? `T${time}` : ""}`)}>
            {i18n.t("datePicker.apply")}
          </Button>
        </Box>
      </Box>
    </Popover>
  </>;
}
